import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';

function AdminModerators() {
  const [moderators, setModerators] = useState([]);
  const [channels, setChannels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    fetchModerators();
    fetchChannels();
  }, []);

  const fetchModerators = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/moderators', { headers });
      const data = await res.json();
      setModerators(data.moderators || []);
    } catch (e) {}
    setLoading(false);
  };

  const fetchChannels = async () => {
    try {
      const res = await fetch('/api/admin/moderators/channels', { headers });
      const data = await res.json();
      setChannels(data.channels || []);
    } catch (e) {}
  };

  // Get unique categories from channels
  const categories = [...new Map(channels.map(ch => [ch.category_id, { category_id: ch.category_id, category_name: ch.category_name }])).values()];

  // Filter channels by selected category
  const filteredChannels = selectedCategory
    ? channels.filter(ch => ch.category_id === parseInt(selectedCategory))
    : [];

  const handleSearch = async () => {
    if (searchQuery.length < 3) return;
    try {
      const res = await fetch(`/api/admin/moderators/search-users?q=${encodeURIComponent(searchQuery)}`, { headers });
      const data = await res.json();
      setSearchResults(data.users || []);
    } catch (e) {}
  };

  const handleAssign = async () => {
    if (!selectedUser || !selectedChannel) {
      alert('Select a user and channel');
      return;
    }
    try {
      const res = await fetch('/api/admin/moderators/assign', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: selectedUser.user_id, channelId: parseInt(selectedChannel) })
      });
      if (res.ok) {
        alert('Moderator assigned!');
        setSelectedUser(null);
        setSelectedChannel('');
        setSearchQuery('');
        setSearchResults([]);
        fetchModerators();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to assign');
      }
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleRemove = async (id, name, channel) => {
    if (!window.confirm(`Remove ${name} from ${channel}?`)) return;
    await fetch('/api/admin/moderators/remove', {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ id })
    });
    fetchModerators();
  };

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
        <h1 style={{ marginBottom: '24px' }}>👥 Content Moderators</h1>

        {/* Assign new moderator */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginBottom: '32px', background: '#fafafa' }}>
          <h3 style={{ marginBottom: '16px' }}>Assign a new moderator</h3>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              type="text" value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search user by email..."
              style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
            <button onClick={handleSearch} style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#6b7280', color: '#fff', cursor: 'pointer' }}>Search</button>
          </div>

          {searchResults.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              {searchResults.map(u => (
                <div key={u.user_id} onClick={() => { setSelectedUser(u); setSearchResults([]); }}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: '6px', background: '#fff', border: '1px solid #e5e7eb', marginBottom: '4px' }}>
                  <strong>{u.name}</strong> — {u.email} (ID: {u.user_id})
                </div>
              ))}
            </div>
          )}

          {selectedUser && (
            <div style={{ padding: '8px 12px', background: '#ede9fe', borderRadius: '8px', marginBottom: '12px' }}>
              Selected: <strong>{selectedUser.name}</strong> ({selectedUser.email})
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <select value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setSelectedChannel(''); }}
              style={{ flex: 1, minWidth: '180px', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}>
              <option value="">1. Select category...</option>
              {categories.map(c => (
                <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
              ))}
            </select>
            <select value={selectedChannel} onChange={(e) => setSelectedChannel(e.target.value)}
              disabled={!selectedCategory}
              style={{ flex: 1, minWidth: '180px', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', opacity: selectedCategory ? 1 : 0.5 }}>
              <option value="">2. Select channel...</option>
              {filteredChannels.map(ch => (
                <option key={ch.channel_id} value={ch.channel_id}>{ch.name}</option>
              ))}
            </select>
            <button onClick={handleAssign} disabled={!selectedUser || !selectedChannel}
              style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', opacity: (!selectedUser || !selectedChannel) ? 0.5 : 1 }}>
              Assign
            </button>
          </div>
        </div>

        {/* Current moderators with stats */}
        <h3 style={{ marginBottom: '12px' }}>Current moderators ({moderators.length})</h3>

        {loading ? <p>Loading...</p> : moderators.length === 0 ? (
          <p style={{ color: '#9ca3af' }}>No moderators assigned yet</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {moderators.map(m => (
              <div key={m.id} style={{ padding: '16px', border: '1px solid #e5e7eb', borderRadius: '10px', background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div>
                    <strong>{m.name}</strong> <span style={{ color: '#6b7280', fontSize: '13px' }}>({m.email})</span>
                  </div>
                  <button onClick={() => handleRemove(m.id, m.name, m.channel_name)}
                    style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', background: '#fee2e2', color: '#dc2626', cursor: 'pointer', fontSize: '12px' }}>
                    Remove
                  </button>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', background: '#ede9fe', color: '#7c3aed', padding: '2px 8px', borderRadius: '6px' }}>{m.channel_name}</span>
                  <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '8px' }}>{m.category_name}</span>
                </div>
                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280' }}>
                  <span>✅ {m.stats.approved} approved</span>
                  <span>✏️ {m.stats.edited} edited</span>
                  <span>❌ {m.stats.rejected} rejected</span>
                  <span>🆕 {m.stats.created} created</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminModerators;
